import {gql} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {Grid} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import AntibodyFormatCreationPane from 'antibodies/format/AntibodyFormatCreationPane';
import {
  AntibodyFormatCreationPermissionQuery,
  AntibodyFormatCreationPermissionQueryVariables,
} from 'antibodies/schema-configuration/__generated/AntibodyFormatCreationPermissionQuery';
import Antibody, {AntibodyProps} from 'antibodies/visualization/Antibody';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ANTIBODIES_ANALYTICS_ACTIONS} from 'constants/analytics-products/AntibodiesAnalyticsConstants';
import {AnalyticsMetadataForContext} from 'constants/AnalyticsConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import PlainSearchInput, {ClearType, BorderRadius} from 'ui-library/PlainSearchInput';
import {InputChangeEvent} from 'util/Types';

export const ANTIBODY_FORMAT_CREATION_PERMISSION_QUERY = gql`
  query AntibodyFormatCreationPermissionQuery @skipAudit @engComponent(name: MOLBIO) {
    currentTenant {
      id
      permissions {
        canCreateAntibodyFormats
      }
    }
  }
`;

export type AntibodyTileProps = AntibodyProps & {disabled: boolean};

export interface AntibodyFormatPickerProps {
  antibodyTileProps: Array<AntibodyTileProps>;
  disabled: boolean;
  selectedFormat: string | null;
  setSelectedFormat: (formatId: string) => void;
  /** "Create new" button will only be shown to users with permission to create new formats. */
  enableCreateNew?: boolean;
  onFormatCreated?: (formatId: string) => Promise<void>;
  /** Analytics are tracked when metadata is provided, and not tracked when metadata is not provided. */
  analyticsMetadata?: AnalyticsMetadataForContext;
}

interface FormatTileProps {
  formatId: string;
  label: string;
  onClick: () => void;
  isSelected: boolean;
  disabled?: boolean;
  isHovered: boolean;
  setHoveredFormatId: (formatId: string | null) => void;
  children: React.ReactNode;
  wrapperClassName?: string;
  labelClassName?: string;
}

function getGlyphWrapperClassName(isSelected: boolean, disabled: boolean, isHovered: boolean): string {
  let stylesToUse = null;
  if (isSelected) {
    stylesToUse = styles.selectedTileGlyphWrapper;
  } else if (disabled && isHovered) {
    stylesToUse = styles.disabledHoveredTileGlyphWrapper;
  } else if (disabled) {
    stylesToUse = styles.disabledTileGlyphWrapper;
  } else if (isHovered) {
    stylesToUse = styles.hoveredTileGlyphWrapper;
  }
  return css(stylesToUse);
}

function FormatTile({
  formatId,
  label,
  onClick,
  isSelected,
  disabled = false,
  isHovered,
  setHoveredFormatId,
  children,
  wrapperClassName,
  labelClassName,
}: FormatTileProps): JSX.Element {
  return (
    <div
      role="presentation"
      onMouseEnter={() => {
        setHoveredFormatId(formatId);
      }}
      onMouseLeave={() => {
        setHoveredFormatId(null);
      }}
    >
      <button
        className={classNames(css(styles.button), {
          [css(styles.disabledButton)]: !isSelected && disabled,
          [css(styles.selectedButton)]: isSelected,
        })}
        onClick={onClick}
        type="button"
        disabled={disabled}
        role="option"
        aria-selected={isSelected}
        aria-label={`${label} format`}
      >
        <div
          className={classNames(
            css(styles.formatTileGlyphWrapper),
            wrapperClassName,
            getGlyphWrapperClassName(isSelected, disabled, isHovered)
          )}
        >
          {children}
        </div>
        <div className={classNames(css(styles.buttonLabel), labelClassName)}>{label}</div>
      </button>
    </div>
  );
}

export default function AntibodyFormatPicker({
  antibodyTileProps: antibodyProps,
  disabled,
  selectedFormat,
  setSelectedFormat,
  enableCreateNew = true,
  onFormatCreated,
  analyticsMetadata,
}: AntibodyFormatPickerProps): JSX.Element {
  assert(
    analyticsMetadata != null || !enableCreateNew,
    'analyticsMetadata must be provided when creation is enabled'
  );

  const creationPermissionQueryResult = useBenchlingQuery<
    AntibodyFormatCreationPermissionQuery,
    AntibodyFormatCreationPermissionQueryVariables
  >(ANTIBODY_FORMAT_CREATION_PERMISSION_QUERY);

  const [query, setQuery] = useState<string>('');
  // Hack to get around the fact that Aphrodite doesn't support nested styling
  const [hoveredFormatId, setHoveredFormatId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const antibodyPropsMatchingQuery = antibodyProps.filter((props) =>
    props.format.name.toLowerCase().includes(query.toLowerCase())
  );

  if (showCreateModal) {
    assert(analyticsMetadata != null, "analyticsMetadata must be provided to show 'create new' pane");
    return (
      <AntibodyFormatCreationPane
        onRequestClose={() => {
          setShowCreateModal(false);
        }}
        onCreate={async (newFormatId) => {
          setShowCreateModal(false);
          await onFormatCreated?.(newFormatId);
        }}
        analyticsMetadata={analyticsMetadata}
      />
    );
  }

  return (
    <div {...getTestProps({component: 'AntibodyFormatPicker', element: 'root'})}>
      <Grid mb="sm">
        <Grid.Col span={6}>
          <PlainSearchInput
            value={query}
            placeholder="Search by format name"
            onChange={(e: InputChangeEvent) => {
              setQuery(e.target.value);
            }}
            onEscape={() => {
              setQuery('');
            }}
            clearType={ClearType.X}
            borderRadius={BorderRadius.LARGE}
          />
        </Grid.Col>
      </Grid>
      <div className={css(styles.picker)} role="listbox" aria-label="Antibody format list">
        {enableCreateNew &&
          creationPermissionQueryResult.type === QueryResultType.SUCCESS &&
          creationPermissionQueryResult.data.currentTenant.permissions.canCreateAntibodyFormats && (
            <FormatTile
              formatId="create-new-format"
              label="Create new"
              onClick={() => {
                analytics.trackEvent(ANTIBODIES_ANALYTICS_ACTIONS.CLICKED_CREATE_NEW_FORMAT, {
                  ...analyticsMetadata,
                  veritas_format_preview_enabled: BenchlingConfig.ANTIBODY_ENABLE_VERITAS_FORMAT_PREVIEW,
                });
                if (BenchlingConfig.ANTIBODY_ENABLE_VERITAS_FORMAT_PREVIEW) {
                  setShowCreateModal(true);
                }
              }}
              isSelected={false}
              isHovered={hoveredFormatId === 'create-new-format'}
              setHoveredFormatId={setHoveredFormatId}
              wrapperClassName={css(styles.createNewGlyphWrapper)}
              labelClassName={css(styles.createNewButtonLabel)}
              disabled={!BenchlingConfig.ANTIBODY_ENABLE_VERITAS_FORMAT_PREVIEW}
            >
              <BenchlingIcon icon="plus" />
            </FormatTile>
          )}
        {!antibodyPropsMatchingQuery.length && (
          <div className={css(styles.noResultsMessageContainer)}>No formats found</div>
        )}
        {antibodyPropsMatchingQuery.map((props) => (
          <FormatTile
            key={props.format.id}
            formatId={props.format.id}
            label={props.format.name}
            onClick={() => {
              // We only need to track format selection on the schema settings page
              if (analyticsMetadata != null) {
                analytics.trackEvent(ANTIBODIES_ANALYTICS_ACTIONS.SELECTED_ANTIBODY_FORMAT_ON_SCHEMA, {
                  ...analyticsMetadata,
                  antibody_format_id: props.format.id,
                });
              }
              setSelectedFormat(props.format.id);
            }}
            isSelected={props.format.id === selectedFormat}
            isHovered={hoveredFormatId === props.format.id}
            disabled={disabled || props.disabled}
            setHoveredFormatId={setHoveredFormatId}
            wrapperClassName={css(styles.formatGlyphWrapper)}
          >
            <Antibody {...props} />
          </FormatTile>
        ))}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  picker: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    padding: '16px',
    border: `1px solid ${Color.GRAY_200}`,
    borderRadius: '4px',
    minHeight: '160px',
    maxHeight: '300px',
    overflowY: 'auto',
    position: 'relative',
  },
  button: {
    backgroundColor: Color.WHITE,
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '4px',
    borderRadius: '4px',
    ':hover': {
      backgroundColor: Color.GRAY_100,
    },
    alignItems: 'center',
    maxWidth: '98px', // 90px glyph + 8px padding
  },
  selectedButton: {
    ':disabled': {
      color: Color.BLACK,
    },
  },
  disabledButton: {
    cursor: 'not-allowed',
  },
  buttonLabel: {
    display: 'flex',
    justifyContent: 'center',
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  createNewButtonLabel: {
    color: Color.BLUE_450,
  },
  formatTileGlyphWrapper: {
    border: `1px solid ${Color.GRAY_100}`,
    borderRadius: '4px',
    width: '90px',
    height: '90px',
    flex: '0 0 90px',
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    padding: '8px',
  },
  selectedTileGlyphWrapper: {
    borderColor: Color.BLUE_500,
  },
  hoveredTileGlyphWrapper: {
    borderColor: Color.GRAY_200,
  },
  disabledTileGlyphWrapper: {
    opacity: 0.75,
    backgroundColor: Color.GRAY_100,
    borderColor: Color.GRAY_100,
  },
  disabledHoveredTileGlyphWrapper: {
    opacity: 0.75,
    backgroundColor: Color.GRAY_50,
    borderColor: Color.GRAY_200,
  },
  formatGlyphWrapper: {
    backgroundColor: Color.GRAY_50,
  },
  createNewGlyphWrapper: {
    backgroundColor: Color.WHITE,
    color: Color.BLUE_500,
    fontSize: '32px',
  },
  noResultsMessageContainer: {
    // Center text in picker element even if the "Create new" button is present
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: Color.GRAY_400,
    pointerEvents: 'none', // Otherwise the "Create new" button will not be selectable
  },
});
